<template>
  <v-container>
    <v-row align="center" justify="center">
      <v-col cols="12" md="6">
        <v-card v-if="!showChangePasswordForm" class="px-4 py-8 mt-12 rounded-xxl">
          <v-card-title>
            <h2 class="font-weight-bold">
              {{ t("access.forgot.title") }}
            </h2>
          </v-card-title>
          <v-card-text>
            <p class="text-left">
              {{ t("access.forgot.description") }}
            </p>
          </v-card-text>
          <v-card-text>
            <v-form class="mb-8" @submit.prevent="submitForgotPasswordRequest">
              <y-text-field
                v-model="email"
                :label="t('access.forgot.emailLabel')"
                required
                autocapitalize="none"
                autocomplete="email"
                :error-messages="
                  (v$.email.$dirty &&
                    v$.email.$invalid &&
                    v$.email.$silentErrors[0].$message.toString()) ||
                  ''
                "
                class="mb-2"
                @input="authStore.resetErrorMessages"
              />
              <y-btn :disabled="isSubmitFormDisabled" type="submit" block>
                {{ t("access.forgot.sendCode") }}
              </y-btn>
            </v-form>
            <y-alert
              v-if="authStore.forgotPassword.errorMessage"
              :title="t('access.forgot.exceptionTitle')"
              type="warning"
            >
              <i18n-t
                tag="div"
                class="mt-4 text-black"
                :keypath="authStore.forgotPassword.errorMessage"
              >
                <template #newLine>
                  <br />
                </template>
              </i18n-t>
            </y-alert>
          </v-card-text>
          <v-card-text>
            <p class="text-left">
              {{ t("access.forgot.backToSignIn") }}
              <router-link to="/signin">
                {{ t("access.forgot.backToSignInAction") }}
              </router-link>
            </p>
          </v-card-text>
        </v-card>

        <v-card v-else class="px-4 py-8 mt-12 rounded-xxl">
          <v-card-title>
            <h2 v-if="codeFromUrl" class="font-weight-bold">
              {{ t("access.forgot.emailSentTitlePrefilled") }}
            </h2>
            <h2 v-else class="font-weight-bold">
              {{ t("access.forgot.emailSentTitle") }}
            </h2>
          </v-card-title>
          <v-card-text>
            <p v-if="codeFromUrl" class="text-left">
              {{ t("access.forgot.emailSentDescriptionPrefilled", { email }) }}
            </p>
            <p v-else class="text-left">
              {{ t("access.forgot.emailSentDescription", { email }) }}
            </p>
          </v-card-text>
          <v-card-text>
            <v-form autocomplete="off" @submit.prevent="submitChangePasswordRequest">
              <y-text-field
                v-model="resetCode"
                :label="t('access.forgot.resetCodeLabel')"
                class="no-inline-number-controls"
                required
                type="number"
                autocapitalize="none"
                autocomplete="off"
                :error-messages="
                  (v$.resetCode.$dirty &&
                    v$.resetCode.$invalid &&
                    v$.resetCode.$silentErrors[0].$message.toString()) ||
                  ''
                "
                @input="authStore.resetErrorMessages"
              />
              <y-text-field
                v-model="newPassword"
                :label="t('access.forgot.newPasswordLabel')"
                type="password"
                required
                autocapitalize="none"
                autocomplete="off"
                :error-messages="
                  (v$.newPassword.$dirty &&
                    v$.newPassword.$invalid &&
                    v$.newPassword.$silentErrors[0].$message.toString()) ||
                  ''
                "
                @input="authStore.resetErrorMessages"
              />

              <y-btn
                :disabled="isChangePasswordFormDisabled"
                type="submit"
                block
                :loading="authStore.forgotPassword.status === LOADING_STATES.LOADING"
              >
                {{ t("access.forgot.resetPassword") }}
              </y-btn>
              <v-alert
                v-if="authStore.forgotPassword.errorMessage"
                density="compact"
                outlined
                type="warning"
                class="mt-4"
              >
                {{ authStore.forgotPassword.errorMessage }}
              </v-alert>
            </v-form>
          </v-card-text>
          <v-card-text>
            <p class="text-left font-weight-medium">
              {{ t("access.forgot.backToSignIn") }}
              <router-link to="/signin" class="text-purple-6">
                {{ t("access.forgot.backToSignInAction") }}
              </router-link>
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, email as emailValidator, helpers } from "@vuelidate/validators";
import { ref, reactive, computed, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { useApplicationStore } from "@/store/application";
import { useAuthenticationStore, LOADING_STATES } from "@/store/authentication";
import { passwordValidations } from "@/utils/custom-validators";

const route = useRoute();
const router = useRouter();
const authStore = useAuthenticationStore();
const applicationStore = useApplicationStore();
const showChangePasswordForm = ref(false);
const email = ref("");
const resetCode = ref("");
const newPassword = ref("");
const { t } = useI18n();

const v$ = useVuelidate(
  {
    email: {
      $autoDirty: true,
      required: helpers.withMessage(t("form.required"), required),
      email: helpers.withMessage(t("form.emailInvalid"), emailValidator),
    },
    resetCode: {
      $autoDirty: true,
      required: helpers.withMessage(t("form.required"), required),
    },
    newPassword: {
      $autoDirty: true,
      ...passwordValidations,
    },
  },
  reactive({ email, resetCode, newPassword }),
);

const codeFromUrl = computed(
  () => !!(route.query.t === "forgot" && route.query.e && route.query.c),
);
onBeforeMount(async () => {
  if (codeFromUrl.value) {
    showChangePasswordForm.value = true;
    email.value = (route.query.e as string).toLowerCase();
    resetCode.value = route.query.c as string;
  }
});

const isSubmitFormDisabled = computed(
  () => v$.value.email.$invalid || authStore.forgotPassword.status === LOADING_STATES.LOADING,
);
const isChangePasswordFormDisabled = computed(
  () =>
    v$.value.resetCode.$invalid ||
    v$.value.newPassword.$invalid ||
    authStore.forgotPassword.status === LOADING_STATES.LOADING,
);

const submitForgotPasswordRequest = async () => {
  const emailLowercase = email.value.toLocaleLowerCase();
  const forgotRequestSuccess = await authStore.initForgotPassword(emailLowercase);
  if (forgotRequestSuccess) {
    showChangePasswordForm.value = true;
  }
};
const submitChangePasswordRequest = async () => {
  const emailLowercase = email.value.toLocaleLowerCase();
  const resetSuccess = await authStore.initConfirmNewPassword(
    emailLowercase,
    resetCode.value,
    newPassword.value,
  );
  if (resetSuccess) {
    router.push("/signin");
    applicationStore.notifyUser({ message: t("access.forgot.passwordResetSuccess") });
  }
};
</script>
